<template>
  <SizeConsolidatedVariant
    v-if="isConsolidatedThirdVariantFlag"
    :id="id"
    :product="product"
    :product-pricing-state="productPricingState"
  />
  <SizeSplitVariant
    v-else-if="isSplitThirdVariantFlag"
    :id="id"
    :product="product"
    :product-pricing-state="productPricingState"
  />
  <SizeSelection
    v-else
    v-model="selectedSizeId"
    v-model:touched="touched"
    v-model:latchkey="selectedLatchkey"
    v-bind="sizeProps"
    :product-id="productId"
    :name="`cta-sizes-${id}`"
    :is-size-dropdown="isSizeDropdown"
    :is-size-chart="isSizeChart"
    :size-chart-img="sizeChartImage"
    :size-chart-id="sizeChartId"
    :brand-name="brandName"
    :type-name="typeName"
    :is-spacing-enabled="isSpacingExperimentEnabled"
    :is-spacing-2-enabled="isSpacingExperiment2Enabled"
    :is-size-chart-align-left="true"
    :class="isSpacingExperimentEnabled ? 'margin-bottom-s' : 'margin-bottom-m'"
    @update:modelValue="onSizeChange"
  >
    <template #after-chips="{ sizeChips }">
      <TrueFit
        v-if="isTrueFitAvailable"
        v-model:touched="touched"
        v-model:selectedSizeId="selectedSizeId"
        :size-chips="sizeChips"
      />
    </template>
  </SizeSelection>
</template>

<script setup>
import { useStore } from 'vuex';
import { ref, computed } from 'vue';
import { useUpc } from '@common-vue/media-card-large';
import { SizeSelection } from '@common-vue/media-card-large/src/modules/size';
import { useTraitAnalyticsData } from '@common-vue/media-card-large/src/composables/useTraitAnalyticsData';
import { useBrand } from '@composables/useBrand';
import { useSizeDropdown } from '@composables/useSizeDropdown';
import { useProductType } from '@composables/useProductType';
import { useCommonProductGetters } from '@composables/useCommonProductGetters';
import { useExperiments } from '@composables/useExperiments';
import { useSelectedLatchkey } from '@modules/latchkey/composables';
import { useSize } from '@modules/size/composables';
import { useFeatureEligibility, useProductFlags } from '@modules/featureControl';
import useMembersOfProduct from '@composables/useMembersOfProduct';
import TrueFit from '@components/TrueFit';
import SizeConsolidatedVariant from '@modules/third-variant/components/SizeConsolidatedVariant';
import SizeSplitVariant from '@modules/third-variant/components/SizeSplitVariant';
import { useProductPrice } from '@modules/price/composables';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  product: {
    /** @type import('vue').PropType<Product> */
    type: Object,
    required: true,
  },
});

const { isMcom, isBcom } = useBrand();
const { showDropdown } = useSizeDropdown();
const { isConsolidatedMaster, isConsolidatedLatchKey } = useProductType({ id: props.id });
const { brandName, typeName } = useCommonProductGetters({ id: props.id });
const { isTrueFitEligible } = useFeatureEligibility();
const selectedLatchkey = useSelectedLatchkey(props.id);
const { sizeProps, selectedSizeId, sizes } = useSize(props.id, props.product);
const { upcData, selectedUpcId } = useUpc(props.id);
const { memberProductsMap } = useMembersOfProduct();
const { isSplitThirdVariantFlag, isConsolidatedThirdVariantFlag } = useProductFlags();
const {
  experimentFlags: { isSpacingExperimentEnabled, isSpacingExperiment2Enabled },
} = useExperiments();

const store = useStore();
const availabilityData = computed(() => store.state.availabilityStore.data);

// TODO: should be exposed from ProductPrice level
// and then passed to onSizeChange on ProductTraits level
const selectedPrice = useProductPrice(props);
const productPricingState = computed(() => [String(selectedPrice.value?.priceTypeId || 'ZERO')]);

const consolidatedProduct = isConsolidatedMaster || isConsolidatedLatchKey;
const selectedProductId = computed(() => upcData.value.upcs[selectedUpcId.value]?.relationships.selectedProduct);
const isSizeDropdown = computed(() => isBcom && (sizeProps.value.isSizeDropdown || (showDropdown.value && sizeProps.value.sizes.length > 1)));

const touched = ref(false);

const isTrueFitAvailable = computed(() => isTrueFitEligible && sizes.value.length > 1);
const selectedConsolidatedSizeChartId = computed(() => memberProductsMap.value[selectedProductId.value]?.traits?.sizes?.sizeChartId);
const selectedConsolidatedSizeChartImage = computed(() => memberProductsMap.value[selectedProductId.value]?.traits?.sizes?.sizeChartImg);
const isSizeChartForConsolidated = computed(() => selectedConsolidatedSizeChartId.value || selectedConsolidatedSizeChartImage.value);

const sizeChartIdTopLevel = props.product.traits?.sizes?.sizeChartId || '';
const sizeChartImageTopLevel = props.product.traits?.sizes?.sizeChartImg || '';
const isSizeChartTopLevel = computed(() => ((!!sizeChartIdTopLevel || !!sizeChartImageTopLevel) && sizeProps.value.sizes.length));

const sizeChartImage = computed(() => (consolidatedProduct ? selectedConsolidatedSizeChartImage.value : sizeChartImageTopLevel));
const isSizeChart = computed(() => (consolidatedProduct ? isSizeChartForConsolidated.value : isSizeChartTopLevel.value));
const productId = computed(() => (consolidatedProduct ? selectedProductId.value : props.id));
const sizeChartId = computed(() => (consolidatedProduct ? selectedConsolidatedSizeChartId.value : sizeChartIdTopLevel));
const { fireTagOnChange } = useTraitAnalyticsData({
  isConsolidatedMaster,
  isMcom,
  product: props.product,
  isSplitThirdVariant: isSplitThirdVariantFlag,
  isConsolidatedThirdVariant: isConsolidatedThirdVariantFlag,
});

// We use @update:modelValue so it only listens to when user click on size
// TOD: move this to ProductTraits scope
function onSizeChange() {
  const selectedSddData = availabilityData.value?.upcNumberSddMap || {};

  fireTagOnChange({
    selectedUpcId: selectedUpcId.value,
    productPricingState: productPricingState.value,
    selectedSddData,
  });
}
</script>
